import {
	ChangeDetectionStrategy,
	Component,
	computed,
	effect,
	ElementRef,
	inject,
	input,
	model,
	OnDestroy,
	signal,
	TemplateRef,
	ViewChild,
	ViewContainerRef,
} from '@angular/core';
import { Overlay, OverlayPositionBuilder, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import {
	addDays,
	addMonths,
	addYears,
	dateDiffInDays,
	getISOWeekAndYear,
	getSchooljaarEind,
	getSchooljaarTovHuidig,
	MAX_DATE,
	maxDate,
	MIN_DATE,
	minDate,
	today,
} from '@cumlaude/shared-utils';
import { toObservable } from '@angular/core/rxjs-interop';
import { CalendarComponent } from '../calendar/calendar.component';
import { DateRange, DateRangeView } from '../utils/date-range';
import { IconComponent } from '@cumlaude/shared-components-icons';
import { map, Observable } from 'rxjs';
import { AsyncPipe, formatDate, NgTemplateOutlet } from '@angular/common';

type CalendarData = { range?: DateRange; hoverRange?: DateRange };

@Component({
	selector: 'app-date-picker-dropdown',
	templateUrl: './date-picker-dropdown.component.html',
	styleUrls: ['./date-picker-dropdown.component.scss'],
	imports: [CalendarComponent, NgTemplateOutlet, AsyncPipe, IconComponent],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DatePickerDropdownComponent implements OnDestroy {
	private readonly overlay = inject(Overlay);
	private readonly overlayPositionBuilder = inject(OverlayPositionBuilder);
	private readonly viewContainerRef = inject(ViewContainerRef);

	inDropdown = input(false);

	view = input<DateRangeView | undefined>(DateRangeView.DAG);
	range = model<DateRange | undefined>(undefined);
	placeholder = input('Kies...');
	allowClear = input(false);

	hoverRange = signal<DateRange | undefined>(undefined);
	customRangeFrom = signal<Date | undefined>(undefined);
	calendarOpen = signal(false);
	calendarMonth = signal(today());
	calendarMonth$ = toObservable(this.calendarMonth);

	// Data die CalendarComponent doorgeeft aan generateDay om de kleuren van de cellen te bepalen
	calendarData$: Observable<CalendarData> = toObservable(computed(() => ({ range: this.range(), hoverRange: this.hoverRange() })));

	aantalSchooljaren = input(10);
	eindSchooljaar = input(getSchooljaarTovHuidig());

	private overlayRef: OverlayRef | undefined;

	protected prettyFormat = prettyFormat;

	@ViewChild('calendar')
	calendar!: TemplateRef<any>;

	/** Anchor voor de calendar dropdown */
	@ViewChild('anchor')
	anchor!: ElementRef<HTMLElement>;

	constructor() {
		effect(() => {
			const date = this.view() === DateRangeView.TOT_EN_MET ? this.range()?.to : this.range()?.from;
			this.calendarMonth.set(date ?? minDate(getSchooljaarEind(this.eindSchooljaar()), today()));
		});
	}

	ngOnDestroy(): void {
		this.closeCalendar();
	}

	toggleOpenCalendar(event: Event) {
		if (!this.calendarOpen()) {
			this.overlayRef = this.overlay.create({
				positionStrategy: this.getPositionStrategy(),
				hasBackdrop: true,
				backdropClass: 'cdk-overlay-transparent-backdrop',
			});
			this.overlayRef.attach(new TemplatePortal(this.calendar, this.viewContainerRef));
			this.overlayRef.backdropClick().subscribe(() => this.closeCalendar());
			this.calendarOpen.set(true);
		} else this.closeCalendar();
		this.hoverRange.set(undefined);
		event.preventDefault();
		event.stopPropagation();
	}

	closeCalendar() {
		this.calendarOpen.set(false);
		this.hoverRange.set(undefined);
		this.customRangeFrom.set(undefined);
		this.overlayRef?.dispose();
	}

	clearDateRange(event: Event) {
		event.preventDefault();
		event.stopPropagation();
		this.range.set(undefined);
		this.closeCalendar();
	}

	private getPositionStrategy() {
		return this.overlayPositionBuilder
			.flexibleConnectedTo(this.anchor)
			.withFlexibleDimensions(false)
			.withPositions([
				{
					originX: 'start',
					originY: 'bottom',
					overlayX: 'start',
					overlayY: 'top',
				},
			]);
	}

	generateDay = (date: Date, { range, hoverRange }: CalendarData) => {
		return {
			date: new Date(date),
			classNames: {
				selected: range && range.from <= date && range.to >= date,
				hovered: hoverRange && hoverRange.from <= date && hoverRange.to >= date,
				clickable: true,
			},
		};
	};

	selectDate(date: Date) {
		if (this.view() === DateRangeView.AANGEPAST) {
			const from = this.customRangeFrom();
			if (from) {
				if (date >= from) {
					this.range.set({ from, to: date });
					this.customRangeFrom.set(undefined);
					this.closeCalendar();
				} else {
					this.customRangeFrom.set(date);
				}
			} else {
				this.customRangeFrom.set(date);
			}
		} else {
			this.range.set(this.getRange(date));
			this.closeCalendar();
		}
	}

	hoverDate(date: Date | null) {
		if (this.view() === DateRangeView.AANGEPAST) {
			const from = this.customRangeFrom();
			if (!date) return;
			this.hoverRange.set(from ? { from, to: maxDate(from, date) } : { from: date, to: date });
		} else {
			this.hoverRange.set(date ? this.getRange(date) : undefined);
		}
	}

	private getRange(date: Date) {
		switch (this.view()) {
			case DateRangeView.WEEK: {
				// zondag = 0, dus +6 % 7 => 0 = maandag
				const dagenNaMaandag = (date.getDay() + 6) % 7;
				return { from: addDays(date, -dagenNaMaandag), to: addDays(date, 6 - dagenNaMaandag) };
			}
			case DateRangeView.VANAF:
				return { from: date, to: MAX_DATE };
			case DateRangeView.TOT_EN_MET:
				return { from: MIN_DATE, to: date };
			default:
				return { from: date, to: date };
		}
	}
	nextMonth = () => this.calendarMonth.update((date) => addMonths(date, 1));
	previousMonth = () => this.calendarMonth.update((date) => addMonths(date, -1));

	nextYear = () => this.calendarMonth.update((date) => addYears(date, 1));
	previousYear = () => this.calendarMonth.update((date) => addYears(date, -1));

	isNextMonthEnabled = this.calendarMonth$.pipe(map((value) => addMonths(value, 1) <= getSchooljaarEind(this.eindSchooljaar())));
	isPreviousMonthEnabled = this.calendarMonth$.pipe(
		map((value) => addMonths(value, -1) > addYears(getSchooljaarEind(this.eindSchooljaar()), -this.aantalSchooljaren()))
	);

	isNextYearEnabled = this.calendarMonth$.pipe(map((value) => addYears(value, 1) <= getSchooljaarEind(this.eindSchooljaar())));
	isPreviousYearEnabled = this.calendarMonth$.pipe(
		map((value) => addYears(value, -1) > addYears(getSchooljaarEind(this.eindSchooljaar()), -this.aantalSchooljaren()))
	);
}

export function prettyFormat(range: DateRange | undefined, view: DateRangeView = DateRangeView.AANGEPAST) {
	if (!range?.from || !range.to) return undefined;

	if (view === DateRangeView.WEEK) {
		const { week: fromWeek, year: fromYear } = getISOWeekAndYear(range.from);
		const { week: toWeek, year: toYear } = getISOWeekAndYear(range.to);
		// zelfde week en precies 6 dagen verschil => range beslaat de hele week
		if (fromWeek === toWeek && fromYear === toYear && dateDiffInDays(range.from, range.to) === 6) return `Wk ${fromWeek} ${fromYear}`;
	}

	if (view === DateRangeView.VANAF) {
		return `Vanaf ${formatDate(range.from, 'd MMM yyyy', 'nl-NL')}`;
	}

	const toFormatted = formatDate(range.to, 'd MMM yyyy', 'nl-NL');
	if (view === DateRangeView.TOT_EN_MET) {
		return `T/m ${toFormatted}`;
	}

	if (range.from.getFullYear() === range.to.getFullYear()) {
		if (range.from.getMonth() === range.to.getMonth()) {
			if (range.from.getDate() === range.to.getDate()) return toFormatted;
			return `${range.from.getDate()} t/m ${toFormatted}`;
		}
		return `${formatDate(range.from, 'd MMM', 'nl-NL')} t/m ${toFormatted}`;
	}

	return `${formatDate(range.from, 'd MMM yyyy', 'nl-NL')} t/m ${toFormatted}`;
}
